/**
 * PostEventMomentumContext
 * =========================
 * Global context for triggering the Post-Event Momentum Screen
 * from anywhere in the app (event teardown, exit handlers, etc.)
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import PostEventMomentumScreen from "./PostEventMomentumScreen";

const PostEventMomentumContext = createContext(null);

export function PostEventMomentumProvider({ children }) {
  const [isVisible, setIsVisible] = useState(false);
  const [eventData, setEventData] = useState(null);

  /**
   * Trigger the momentum screen after a ticketed event
   * 
   * @param {Object} data
   * @param {string} data.eventId - The event ID
   * @param {string} data.eventType - "video" or "text"
   * @param {boolean} data.wasTicketed - Must be true to show screen
   * @param {string} data.exitReason - "ended" | "early_exit" | "minutes_exhausted"
   */
  const triggerMomentumScreen = useCallback((data) => {
    // LOCKED: Only trigger for ticketed events
    if (!data?.wasTicketed) {
      console.log("[PostEventMomentum] Skipped - not a ticketed event");
      return;
    }

    // LOCKED: Never trigger for lounge or non-event contexts
    if (data?.context === "lounge") {
      console.log("[PostEventMomentum] Skipped - lounge exit");
      return;
    }

    console.log("[PostEventMomentum] Triggering screen", data);
    setEventData({
      eventId: data.eventId || "unknown",
      eventType: data.eventType || "video",
      wasTicketed: true,
      exitReason: data.exitReason || "ended",
    });
    setIsVisible(true);
  }, []);

  const dismissMomentumScreen = useCallback(() => {
    setIsVisible(false);
    setEventData(null);
  }, []);

  return (
    <PostEventMomentumContext.Provider
      value={{
        triggerMomentumScreen,
        dismissMomentumScreen,
        isVisible,
      }}
    >
      {children}
      
      {/* Render the momentum screen when triggered */}
      {isVisible && eventData && (
        <PostEventMomentumScreen
          eventId={eventData.eventId}
          eventType={eventData.eventType}
          wasTicketed={eventData.wasTicketed}
          exitReason={eventData.exitReason}
          onDismiss={dismissMomentumScreen}
        />
      )}
    </PostEventMomentumContext.Provider>
  );
}

/**
 * Hook to access the momentum screen trigger
 */
export function usePostEventMomentum() {
  const context = useContext(PostEventMomentumContext);
  if (!context) {
    console.warn("[usePostEventMomentum] Must be used within PostEventMomentumProvider");
    return {
      triggerMomentumScreen: () => {},
      dismissMomentumScreen: () => {},
      isVisible: false,
    };
  }
  return context;
}

export default PostEventMomentumContext;
